import { HttpStatusCode } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Params, RouterLink } from '@angular/router';
import { StorageService } from 'app/00_modules/core/services/storage.service';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { HttpError } from '../00_modules/core/common/http-error';
import { RouterService } from '../00_modules/core/services/router.service';
import { UserService } from '../00_modules/core/services/user.service';
import { AppUser } from '../00_modules/core/user/AppUser';
import { Role } from '../00_modules/core/user/Role';
import { InfoWindowDataService } from '../00_modules/master-data/services/info-window-data.service';
import { InfoWindowDialogService } from '../00_modules/shared/services/info-window-dialog.service';
import { UserFacade } from '../store/facade/user.facade';
import { JeVersionComponent } from '../00_modules/shared/components/version/version.component';
import { TranslateModule } from '@ngx-translate/core';
import { UserSettingsService } from '../00_modules/user-profile/services/user-settings.service';
import { BsmInput } from '@bsm-ui-lib/bsm-ui';
import { ButtonComponent } from '../00_modules/shared/components/buttons/button/button.component';
import { MessageBoxComponent } from '../00_modules/shared/components/message-box/message-box.component';

@Component({
    selector: 'je-login',
    templateUrl: './login.component.html',
    imports: [ReactiveFormsModule, RouterLink, JeVersionComponent, TranslateModule, BsmInput, ButtonComponent, MessageBoxComponent],
})
export class LoginComponent implements OnInit, OnDestroy {
    public showLogout = false;
    public errorMessage: string;
    public user: AppUser = new AppUser();

    loginForm = new FormGroup({
        accountName: new FormControl('', Validators.required),
        password: new FormControl('', Validators.required),
    });
    public isChristmas: boolean;
    public isEaster: boolean;
    public isSummer: boolean;
    public isHalloween: boolean;
    public isCarnival: boolean;
    public isAshWednesday: boolean;
    public isStarWars: boolean;
    public isFootball: boolean;
    public isMaintenance: boolean;
    private returnUrl: string;
    private showLogoutSubscription: Subscription;

    constructor(
        private route: ActivatedRoute,
        private routerService: RouterService,
        private userService: UserService,
        private storageService: StorageService,
        private userFacade: UserFacade,
        private infoWindowDialogService: InfoWindowDialogService,
        private infoWindowDataService: InfoWindowDataService,
        private userSettingsService: UserSettingsService,
    ) {
        this.user.accountName = '';
        this.user.password = '';
    }

    public async ngOnInit(): Promise<void> {
        this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
        if (await this.userService.isLoggedIn()) {
            this.routerService.navigateToDashboard();
        }
        this.showLogoutSubscription = this.route.params.subscribe(params => this.setShowLogout(params));
        this.detectHoliday();
    }

    public detectHoliday(): void {
        let currentDate = new Date();
        let currentYear = new Date().getFullYear();
        let christmasStart = new Date(currentYear + '-12-01');
        let christmasEnd = new Date(currentYear + '-12-27');
        let easterStart = new Date(currentYear + '-03-25');
        let easterEnd = new Date(currentYear + '-04-30');
        let summerStart = new Date(currentYear + '-06-01');
        let summerEnd = new Date(currentYear + '-09-01');
        let halloweenStart = new Date(currentYear + '-10-01');
        let halloweenEnd = new Date(currentYear + '-11-05');
        let starwarsDay = new Date(currentYear + '-05-04');
        let footballstart = new Date('2024-06-14');
        let footballEnd = new Date('2024-07-14');

        if (currentDate > christmasStart && currentDate < christmasEnd) {
            this.isChristmas = true;
        }

        if (currentDate > easterStart && currentDate < easterEnd) {
            this.isEaster = true;
        }

        if (currentDate > summerStart && currentDate < summerEnd) {
            this.isSummer = true;
        }

        if (currentDate > halloweenStart && currentDate < halloweenEnd) {
            this.isHalloween = true;
        }
        if (currentDate > footballstart && currentDate < footballEnd) {
            this.isFootball = true;
        }

        let easterSunday = new Date(this.gaussEaster(new Date().getFullYear()));
        if (currentDate > this.getCarnivalStart(easterSunday) && currentDate < this.getCarnivalEnd(easterSunday)) {
            this.isCarnival = true;
        }
        if (
            currentDate.getDate() == this.getAshWednesday(easterSunday).getDate() &&
            currentDate.getMonth() == this.getAshWednesday(easterSunday).getMonth() &&
            currentDate.getFullYear() == this.getAshWednesday(easterSunday).getFullYear()
        ) {
            this.isAshWednesday = true;
        }

        if (currentDate.getDate() == starwarsDay.getDate() && currentDate.getMonth() == starwarsDay.getMonth() && currentDate.getFullYear() == starwarsDay.getFullYear()) {
            this.isStarWars = true;
        }
    }

    // Function calculates and prints
    // easter date for given year Y
    public gaussEaster(year: number): string {
        let A, B, C, P, Q, M, N, D, E;

        // All calculations done
        // on the basis of
        // Gauss Easter Algorithm
        A = year % 19;
        B = year % 4;
        C = year % 7;
        P = Math.floor(year / 100.0);

        Q = Math.floor((13 + 8 * P) / 25.0);
        M = Math.floor(15 - Q + P - Math.floor(P / 4)) % 30;
        N = Math.floor(4 + P - Math.floor(P / 4)) % 7;
        D = Math.floor(19 * A + M) % 30;
        E = Math.floor(2 * B + 4 * C + 6 * D + N) % 7;

        let days = Math.floor(22 + D + E);

        // A corner case,
        // when D is 29
        if (D == 29 && E == 6) {
            return year + '-04-19';
        }
        // Another corner case,
        // when D is 28
        else if (D == 28 && E == 6) {
            return year + '-04-18';
        } else {
            // If days > 31, move to April
            // April = 4th Month
            if (days > 31) {
                return year + '-04-' + (days - 31);
            } else {
                // Otherwise, stay on March
                // March = 3rd Month
                return year + '-03-' + days;
            }
        }
    }

    // Carnival starts 46 days bevor easter sunday
    public getCarnivalStart(easterSunday: Date): Date {
        let AshWednesday = this.getAshWednesday(easterSunday);
        let CarnivalStart = new Date(AshWednesday.getTime() - 6 * 24 * 60 * 60 * 1000);
        return CarnivalStart;
    }

    public getCarnivalEnd(easterSunday: Date): Date {
        let AshWednesday = this.getAshWednesday(easterSunday);
        let CarnivalEnd = new Date(AshWednesday.getTime() - 1 * 24 * 60 * 60 * 1000);
        return CarnivalEnd;
    }

    public getAshWednesday(easterSunday: Date): Date {
        let AshWednesday = new Date(easterSunday.getTime() - 46 * 24 * 60 * 60 * 1000);
        return AshWednesday;
    }

    public ngOnDestroy(): void {
        if (this.showLogoutSubscription != null) {
            this.showLogoutSubscription.unsubscribe();
        }
    }

    public onSubmit(): void {
        if (this.loginForm.valid) {
            this.user.accountName = this.loginForm.value.accountName;
            this.user.password = this.loginForm.value.password;
            this.userService.getAuthenticatedUser(this.user).subscribe({
                next: (user: AppUser) => {
                    this.userService.getUserRoles().subscribe((rs: Role[]) => {
                        this.userFacade.setUser(user);
                        if (rs.length > 0) {
                            const userRole = rs[0];
                            this.userFacade.setUserRole(userRole);

                            this.storageService.setRoleValues(userRole.id, userRole.name);
                        }

                        this.userSettingsService.initialize(user.id);

                        this.infoWindowDataService
                            .getInfoWindowData()
                            .pipe(take(1))
                            .subscribe(data => {
                                this.infoWindowDialogService.showDialog(data.infoText, data.visibilityId);
                            });

                        let group: string;
                        this.userFacade.userGroup$.pipe(take(1)).subscribe(g => (group = g));
                        if (group === AppUser.GROUP_CONTRACTOR || group === AppUser.GROUP_CONTRACTOR_ADMIN) {
                            this.routerService.gotoHome();
                        }

                        if (group === AppUser.GROUP_CUSTOMER) {
                            this.routerService.navigateToCustomerSelection();
                        }

                        if (this.returnUrl) {
                            window.location.href = `/#${this.returnUrl}`;
                            window.location.reload();
                        }
                    });
                },
                error: (error: HttpError) => {
                    if (!error) {
                        this.errorMessage = 'LOGIN.ERROR.SERVER_NOT_REACHABLE';
                        return;
                    }

                    if (error.status === HttpStatusCode.BadRequest || error.status === HttpStatusCode.Unauthorized) {
                        this.errorMessage = 'LOGIN.ERROR';
                    } else if (error.status === HttpStatusCode.Locked) {
                        this.errorMessage = 'LOGIN.ERROR.MAINTENANCE';
                        this.isMaintenance = true;
                    }
                },
            });
        }
    }

    private setShowLogout(params: Params): void {
        const para = params['showLogout'];
        if (para != null && para === 'true') {
            this.showLogout = true;

            // The router recognizes that one is asking to navigate to the same component,
            // so it will change the parameters without actually navigating away.
            // The result is that after a "reload" the optional parameter is removed from the URL.
            this.routerService.navigateToLogin();
        } else {
            this.showLogout = false;
        }
    }
}
